/**
 * The body measurements every detector reads (CAM-02, architecture §20).
 *
 * **Slice C owns the real thing.** Calibration (game design spec §13: stand in lane → neutral pose
 * → raise hands → crouch → move in lane → jump → confirm) is what turns a particular child in a
 * particular room into these numbers, and it produces them per player, per session, stored nowhere.
 * Slice B needs the *shape* now, so the detectors can be written and proved against something they
 * will still be reading when the real measurements arrive.
 *
 * So every value in {@link DEFAULT_POSE_CALIBRATION} is an invented placeholder in the D-010/D-023
 * tradition — a plausible average five-year-old standing 1.5–2.5 m from a webcam, not a
 * requirement, and not measured from any child. They exist so a synthetic test suite has a body to
 * describe; a real session replaces them wholesale.
 *
 * Two conventions are load-bearing and are *not* placeholders:
 *
 *   - **Frame space** for everything vertical: `y` grows downward, as MediaPipe reports it, so a
 *     jump makes `hipY` smaller and a crouch makes it larger.
 *   - **Mirrored space** for the lane bounds. The feed a child sees is mirrored (MVP notes §5), so
 *     lanes are expressed on the side of the screen the child watches themselves occupy, and the
 *     detector mirrors the sensor's x before comparing. Recording the bounds any other way is
 *     precisely how left and right end up swapped for a five-year-old (CAM-04).
 */

/**
 * A `CalibrationProfile`'s `baselineMeasurements` (architecture §20), typed for the detectors that
 * consume them. Distances are in normalised frame units unless the name says otherwise; anything
 * named `…Scale` is a multiple of {@link PoseCalibrationThresholds.bodyScale}, so a child who is
 * closer to the camera is not a child who jumps higher.
 */
export interface PoseCalibrationThresholds {
  /** Hip midpoint y while standing neutrally: the baseline a jump rises from and a crouch drops from. */
  standingHipY: number;
  /** Shoulder-to-hip distance while standing. The unit every other measurement is expressed in. */
  bodyScale: number;
  /** Lane boundaries in **mirrored** x — the side of the screen the child sees themselves on. */
  laneBounds: {
    /** Mirrored x below this is the left of the screen. */
    leftMax: number;
    /** Mirrored x above this is the right of the screen. */
    rightMin: number;
  };
  /** How far past a boundary the body must travel before a lane change is believed. */
  laneHysteresis: number;
  /** Hip rise, in body scales, that counts as leaving the ground. */
  jumpRiseScale: number;
  /** Hip drop, in body scales, that counts as a crouch. */
  crouchDropScale: number;
  /** How far above the shoulder line a wrist must be, in body scales, to count as raised. */
  handsRaiseScale: number;
  /**
   * How far above the shoulder line a wrist must be for a *large reach* (GDS §15, Compact mode).
   *
   * Deliberately larger than {@link PoseCalibrationThresholds.handsRaiseScale} and deliberately its
   * own number: raising a hand to answer "yes" and stretching up to charge a web are two different
   * requests, and a child who is asked for four big reaches should not have four small lifts
   * counted for them. Measured by the Compact calibration's step 6 (`reach`); the default below is
   * an invented placeholder like the rest of this file.
   */
  substituteReachScale: number;
  /** How many wrists must be up for "hands raised". */
  handsRaisedWristCount: number;
  /** Torso speed, in body scales per second, at or below which the child is not moving. */
  stillnessSpeedScale: number;
  /**
   * How much of an entry threshold must remain before a held posture is released.
   *
   * One number for every detector on purpose: a posture that is entered and released at the same
   * threshold chatters, and a child holding a crouch would produce a stream of stand/crouch pairs
   * from nothing but sensor noise.
   */
  hysteresisFraction: number;
}

export const DEFAULT_POSE_CALIBRATION: PoseCalibrationThresholds = {
  standingHipY: 0.62,
  bodyScale: 0.22,
  laneBounds: { leftMax: 0.38, rightMin: 0.62 },
  laneHysteresis: 0.04,
  jumpRiseScale: 0.35,
  crouchDropScale: 0.35,
  handsRaiseScale: 0.15,
  // A large reach is a bigger ask than a raised hand, so the placeholder is bigger. Invented.
  substituteReachScale: 0.3,
  // One arm is enough by default: a five-year-old says yes with whichever hand is free, and a
  // detector that insisted on both would tell a child who answered that they had not.
  handsRaisedWristCount: 1,
  stillnessSpeedScale: 0.25,
  hysteresisFraction: 0.5,
};
